import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { updateFormData, saveDossier } from "../../redux/dispatch";
import useRubriqueNav from "../../hooks/useRubriqueNav";
import RubriqueNav from "../shared/RubriqueNav";
import SaveLoader from "../shared/SaveLoader";
import ConfidenceLabel from "../shared/ConfidenceLabel";
import useConfidence from "../../hooks/useConfidence";
import "./InfosReferences.css";
import toast from "react-hot-toast";
import { API_BASE } from "../../config/api";
import noImage from "../../assets/no-image.svg";

const RUBRIQUE = "infos_references";

const INITIAL_FORM = {
  hopital: "",
  service_ref: "",
  service_email: "",
  service_telephone: "",
  medecin_nom: "",
  medecin_email: "",
  medecin_mobile: "",
  medecin_adresse: "",
  medecin_unite: "",
  date_reference: "",
  date_arrivee: "",
  date_prise_en_charge: "",
  medecin_reception: "",
};

const InfosReferences = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id }   = useParams();
  const [saving, setSaving] = useState(false);

  const rawImages = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.images || []);
  const IMAGES = rawImages.length > 0
    ? rawImages.map((img) => `${API_BASE}/${img.chemin_image}`)
    : [noImage];

  const { currentDossier, list } = useSelector((s) => s.dossiers);
  const dossier = currentDossier || list.find((d) => d.id === id) || null;

  const savedData = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.data || {});

  const [form, setForm] = useState({ ...INITIAL_FORM, ...savedData });
  const formRef = useRef(form);

  useEffect(() => { setForm({ ...INITIAL_FORM, ...savedData }); }, [id]);
  useEffect(() => { formRef.current = form; }, [form]);

  const [imgIndex, setImgIndex] = useState(0);

// APRÈS
const { goNext, goPrev, goTo, suivante, precedente, indexCourant, total } = useRubriqueNav(RUBRIQUE);
  // ── Confidence ───────────────────────────────────────────────────────────────
  const { getConfidence } = useConfidence(id, RUBRIQUE);
  const gc = (field) => getConfidence("data", field);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    dispatch(updateFormData(id, RUBRIQUE, "data", formRef.current));
  };

  useEffect(() => {
    return () => { handleSave(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveAll = async () => {
    setSaving(true);
    dispatch(updateFormData(id, RUBRIQUE, "data", form));
    try {
      const result = await dispatch(saveDossier());
      toast.success(result.message);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({ ...INITIAL_FORM, ...savedData });
  };

  if (!dossier) {
    return (
      <div className="ir-no-dossier">
        <p>Aucun dossier sélectionné.</p>
        <button onClick={() => navigate("/")}>Retour à la liste</button>
      </div>
    );
  }

  return (
    <div className="ir-page">
      {saving && <SaveLoader />}

      {/* ── Header ── */}
      <header className="ir-header">
        <div className="ir-header-left">
          <button className="ir-back-btn" onClick={() => navigate("/")}>← Retour à la liste</button>
          <div>
            <h1 className="ir-title">Dossier {dossier.numero_dossier}</h1>
            <p className="ir-subtitle">Patient : {dossier.patient} &nbsp;|&nbsp; Service : {dossier.service}</p>
          </div>
        </div>
        <button className="ir-save-btn" onClick={handleSaveAll} disabled={saving}>
          Sauvegarder les modifications
        </button>
      </header>

      {/* ── Corps ── */}
      <div className="ir-body">

        {/* ── Colonne gauche : image ── */}
        <aside className="ir-image-panel">
          <div className="ir-image-header">
            <span className="ir-rubrique-label">Infos Références</span>
            <span className="ir-image-counter">Image {imgIndex + 1} / {IMAGES.length}</span>
          </div>
          <div className="ir-image-viewer">
            <img src={IMAGES[imgIndex]} alt={`Page ${imgIndex + 1}`} className="ir-image" />
          </div>
          <div className="ir-image-nav">
            <button className="ir-nav-btn" onClick={() => setImgIndex((i) => Math.max(0, i - 1))} disabled={imgIndex === 0}>‹</button>
            <div className="ir-image-dots">
              {IMAGES.map((_, i) => (
                <span key={i} className={`ir-dot ${i === imgIndex ? "ir-dot--active" : ""}`} onClick={() => setImgIndex(i)} />
              ))}
            </div>
            <button className="ir-nav-btn" onClick={() => setImgIndex((i) => Math.min(IMAGES.length - 1, i + 1))} disabled={imgIndex === IMAGES.length - 1}>›</button>
          </div>
        </aside>

        {/* ── Colonne droite : formulaire ── */}
        <main className="ir-form-panel">
          <div className="ir-form-scroll">

            {/* Section : Structure de Référence */}
            <section className="ir-section">
              <h2 className="ir-section-title">Structure de Référence</h2>
              <div className="ir-fields">
                <div className="ir-field-group">
                  <label>Hôpital <ConfidenceLabel confidence={gc("hopital")} /></label>
                  <input
                    type="text"
                    placeholder="Nom de l'hôpital"
                    value={form.hopital ?? ""}
                    onChange={(e) => handleChange("hopital", e.target.value)}
                    style={{ borderColor: gc("hopital").color || undefined }}
                  />
                </div>
                <div className="ir-field-group">
                  <label>Service <ConfidenceLabel confidence={gc("service_ref")} /></label>
                  <input
                    type="text"
                    placeholder="Nom du service"
                    value={form.service_ref ?? ""}
                    onChange={(e) => handleChange("service_ref", e.target.value)}
                    style={{ borderColor: gc("service_ref").color || undefined }}
                  />
                </div>
                <div className="ir-field-group">
                  <label>E-mail de la structure <ConfidenceLabel confidence={gc("service_email")} /></label>
                  <input type="email" placeholder="email@structure.com"
                    value={form.service_email ?? ""}
                    onChange={(e) => handleChange("service_email", e.target.value)}
                    style={{ borderColor: gc("service_email").color || undefined }} />
                </div>
                <div className="ir-field-group">
                  <label>Téléphone de la structure <ConfidenceLabel confidence={gc("service_telephone")} /></label>
                  <input type="tel" placeholder="+225 00 00 00 00 00"
                    value={form.service_telephone ?? ""}
                    onChange={(e) => handleChange("service_telephone", e.target.value)}
                    style={{ borderColor: gc("service_telephone").color || undefined }} />
                </div>
              </div>
            </section>

            <div className="ir-divider" />

            {/* Section : Médecin Référent */}
            <section className="ir-section">
              <h2 className="ir-section-title">Médecin Référent</h2>
              <div className="ir-fields">
                <div className="ir-field-group">
                  <label>Nom <ConfidenceLabel confidence={gc("medecin_nom")} /></label>
                  <input
                    type="text"
                    placeholder="Nom du médecin référent"
                    value={form.medecin_nom ?? ""}
                    onChange={(e) => handleChange("medecin_nom", e.target.value)}
                    style={{ borderColor: gc("medecin_nom").color || undefined }}
                  />
                </div>
                <div className="ir-field-group">
                  <label>E-mail <ConfidenceLabel confidence={gc("medecin_email")} /></label>
                  <input
                    type="email"
                    placeholder="email@exemple.com"
                    value={form.medecin_email ?? ""}
                    onChange={(e) => handleChange("medecin_email", e.target.value)}
                    style={{ borderColor: gc("medecin_email").color || undefined }}
                  />
                </div>
                <div className="ir-field-group">
                  <label>Mobile <ConfidenceLabel confidence={gc("medecin_mobile")} /></label>
                  <input
                    type="tel"
                    placeholder="+225 00 00 00 00 00"
                    value={form.medecin_mobile ?? ""}
                    onChange={(e) => handleChange("medecin_mobile", e.target.value)}
                    style={{ borderColor: gc("medecin_mobile").color || undefined }}
                  />
                </div>
                <div className="ir-field-group">
                  <label>Adresse Postale <ConfidenceLabel confidence={gc("medecin_adresse")} /></label>
                  <input
                    type="text"
                    placeholder="Adresse postale"
                    value={form.medecin_adresse ?? ""}
                    onChange={(e) => handleChange("medecin_adresse", e.target.value)}
                    style={{ borderColor: gc("medecin_adresse").color || undefined }}
                  />
                </div>
                <div className="ir-field-group">
                  <label>Unité médicale <ConfidenceLabel confidence={gc("medecin_unite")} /></label>
                  <input type="text" placeholder="Unité médicale"
                    value={form.medecin_unite ?? ""}
                    onChange={(e) => handleChange("medecin_unite", e.target.value)}
                    style={{ borderColor: gc("medecin_unite").color || undefined }} />
                </div>
              </div>
            </section>

            <div className="ir-divider" />

            {/* Section : Dates & Réception */}
            <section className="ir-section">
              <h2 className="ir-section-title">Dates & Réception</h2>
              <div className="ir-fields">
                <div className="ir-row">
                  <div className="ir-field-group">
                    <label>Date de Référence <ConfidenceLabel confidence={gc("date_reference")} /></label>
                    <input
                      type="date"
                      value={form.date_reference ?? ""}
                      onChange={(e) => handleChange("date_reference", e.target.value)}
                      style={{ borderColor: gc("date_reference").color || undefined }}
                    />
                  </div>
                  <div className="ir-field-group">
                    <label>Date d'Arrivée dans l'Unité <ConfidenceLabel confidence={gc("date_arrivee")} /></label>
                    <input
                      type="date"
                      value={form.date_arrivee ?? ""}
                      onChange={(e) => handleChange("date_arrivee", e.target.value)}
                      style={{ borderColor: gc("date_arrivee").color || undefined }}
                    />
                  </div>
                  <div className="ir-field-group">
                    <label>Date de prise en charge <ConfidenceLabel confidence={gc("date_prise_en_charge")} /></label>
                    <input
                      type="date"
                      value={form.date_prise_en_charge ?? ""}
                      onChange={(e) => handleChange("date_prise_en_charge", e.target.value)}
                      style={{ borderColor: gc("date_prise_en_charge").color || undefined }}
                    />
                  </div>
                </div>
                <div className="ir-field-group">
                  <label>Médecin qui a reçu l'Enfant <ConfidenceLabel confidence={gc("medecin_reception")} /></label>
                  <input
                    type="text"
                    placeholder="Nom du médecin recevant"
                    value={form.medecin_reception ?? ""}
                    onChange={(e) => handleChange("medecin_reception", e.target.value)}
                    style={{ borderColor: gc("medecin_reception").color || undefined }}
                  />
                </div>
              </div>
            </section>
          </div>

          {/* ── Actions ── */}
          {/* <div className="ir-form-actions">
            <button className="ir-btn-save" onClick={handleSave}>Enregistrer</button>
            <button className="ir-btn-cancel" onClick={handleCancel}>Annuler</button>
          </div> */}

          {/* ── Navigation entre rubriques ── */}

<RubriqueNav goNext={goNext} goPrev={goPrev} goTo={goTo} suivante={suivante} precedente={precedente} indexCourant={indexCourant} total={total} />
        </main>
      </div>
    </div>
  );
};

export default InfosReferences;